/**
 * Registry of editor style fields that {@link ActionStyleNode} can set on the layout node
 * owning a behavior graph. Add entries here to expose new draggable action nodes.
 */

export type EditorStyleActionKey =
  | "visibility"
  | "backgroundColor"
  | "backgroundImage"
  | "color"
  | "filter"
  | "boxShadow"
  | "textShadow"
  | "fontSize"
  | "fontFamily"
  | "fontWeight"
  | "textAlign"
  | "textTransform"
  | "whiteSpace"
  | "textDecorationLine"
  | "textDecorationColor"
  | "textDecorationStyle"
  | "textDecorationThickness"
  | "border"
  | "borderRadius"
  | "opacity"
  | "padding";

/** How the user edits the value inside the action node (no separate data port). */
export type EditorStyleValueInputKind =
  | "boolean"
  | "number"
  | "string"
  | "fontFamily"
  | "fontWeight"
  | "textAlign"
  | "textTransform"
  | "whiteSpace"
  | "textDecorationLine"
  | "textDecorationColor"
  | "textDecorationStyle"
  | "textDecorationThickness"
  | "color"
  | "gradient"
  | "filter"
  | "boxShadow"
  | "textShadow"
  | "border"
  | "borderRadius"
  | "spacing";

export interface EditorStyleActionDefinition {
  key: EditorStyleActionKey;
  inputKind: EditorStyleValueInputKind;
}

/**
 * Ordered list of style actions exposed in the behavior editor sidebar.
 * Keep in sync with the layout style keys.
 */
/**
 * Maps each action to the matching `licensedPortal.layoutEditor.properties` field used for labels.
 */
export const EDITOR_STYLE_ACTION_PROPERTY_LABEL_KEY: Record<
  EditorStyleActionKey,
  string
> = {
  visibility: "visibility",
  backgroundColor: "backgroundColor",
  backgroundImage: "backgroundImage",
  color: "color",
  filter: "filter",
  boxShadow: "boxShadow",
  textShadow: "textShadow",
  fontSize: "fontSize",
  fontFamily: "fontFamily",
  fontWeight: "fontWeight",
  textAlign: "textAlign",
  textTransform: "textTransform",
  whiteSpace: "whiteSpace",
  textDecorationLine: "textDecorationLine",
  textDecorationColor: "textDecorationColor",
  textDecorationStyle: "textDecorationStyle",
  textDecorationThickness: "textDecorationThickness",
  border: "border",
  borderRadius: "borderRadius",
  opacity: "opacity",
  padding: "padding",
};

export const EDITOR_STYLE_ACTIONS: readonly EditorStyleActionDefinition[] = [
  { key: "visibility", inputKind: "boolean" },
  { key: "backgroundColor", inputKind: "color" },
  { key: "backgroundImage", inputKind: "gradient" },
  { key: "color", inputKind: "color" },
  { key: "filter", inputKind: "filter" },
  { key: "boxShadow", inputKind: "boxShadow" },
  { key: "textShadow", inputKind: "textShadow" },
  { key: "fontSize", inputKind: "number" },
  { key: "fontFamily", inputKind: "fontFamily" },
  { key: "fontWeight", inputKind: "fontWeight" },
  { key: "textAlign", inputKind: "textAlign" },
  { key: "textTransform", inputKind: "textTransform" },
  { key: "whiteSpace", inputKind: "whiteSpace" },
  { key: "textDecorationLine", inputKind: "textDecorationLine" },
  { key: "textDecorationColor", inputKind: "color" },
  { key: "textDecorationStyle", inputKind: "textDecorationStyle" },
  { key: "textDecorationThickness", inputKind: "number" },
  { key: "border", inputKind: "border" },
  { key: "borderRadius", inputKind: "borderRadius" },
  { key: "opacity", inputKind: "number" },
  { key: "padding", inputKind: "spacing" },
] as const;

const definitionByKey = new Map<
  EditorStyleActionKey,
  EditorStyleActionDefinition
>(EDITOR_STYLE_ACTIONS.map((def) => [def.key, def]));

/**
 * Returns the definition for a style action key, or undefined if unknown.
 */
export function getEditorStyleActionDefinition(
  key: EditorStyleActionKey,
): EditorStyleActionDefinition | undefined {
  return definitionByKey.get(key);
}

/**
 * Default literal stored on a newly dropped action node.
 */
export function getDefaultStyleValueForAction(
  def: EditorStyleActionDefinition,
):
  | string
  | number
  | boolean
  | { border: { top: string; right: string; bottom: string; left: string } }
  | {
      radius: {
        topLeft: string;
        topRight: string;
        bottomRight: string;
        bottomLeft: string;
      };
    }
  | {
      margin: {
        scope: "all" | "top" | "right" | "bottom" | "left";
        value: number;
      };
      padding: {
        scope: "all" | "top" | "right" | "bottom" | "left";
        value: number;
      };
    } {
  switch (def.inputKind) {
    case "boolean":
      return true;
    case "number":
      if (def.key === "opacity") {
        return 1;
      }
      if (def.key === "fontSize") {
        return 16;
      }
      return 0;
    case "string":
      return "";
    case "fontFamily":
      return "Arial";
    case "fontWeight":
      return "normal";
    case "textAlign":
      return "left";
    case "textTransform":
      return "none";
    case "whiteSpace":
      return "normal";
    case "textDecorationLine":
      return "none";
    case "textDecorationStyle":
      return "solid";
    case "textDecorationThickness":
      return 1;
    case "color":
      return "#00000000";
    case "gradient":
      return "linear-gradient(90deg, #000000 0%, #ffffff 100%)";
    case "filter":
      return "none";
    case "boxShadow":
      return "none";
    case "textShadow":
      return "none";
    case "border":
      return {
        border: {
          top: "1px solid #000000",
          right: "1px solid #000000",
          bottom: "1px solid #000000",
          left: "1px solid #000000",
        },
      };
    case "borderRadius":
      return {
        radius: {
          topLeft: "0px",
          topRight: "0px",
          bottomRight: "0px",
          bottomLeft: "0px",
        },
      };
    case "spacing":
      return {
        margin: { scope: "all", value: 0 },
        padding: { scope: "all", value: 0 },
      };
    default:
      return "";
  }
}
